import { createFileRoute, Link } from '@tanstack/react-router'
import { format, isAfter, subDays } from 'date-fns'
import { ArrowRightIcon, CalendarCheckIcon, LeafIcon, ReceiptTextIcon, WalletIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useFriskvardPayments } from '@/api/hooks/useFriskvardPayments'
import { useMembership } from '@/api/hooks/useMembership'
import { useOrganizationQuery } from '@/api/hooks/useOrganization'
import { useOrganizationStatistics } from '@/api/hooks/useOrganizationStatistics'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import PageLayout from '@/components/layout/page-layout'
import { GenericListSkeleton } from '@/components/molecules/generic-skeletons'
import { KPICard } from '@/components/molecules/kpi-card'
import FriskvardTable from '@/components/organisms/friskvard-table/friskvard-table'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/_authenticated/overview')({
  component: OverviewPage,
})

const formatKr = (value: number) => `${value.toLocaleString('sv-SE')} kr`
const formatDate = (value: string) => format(new Date(value), 'd MMM yyyy')

const OverviewSkeleton = () => (
  <div className="flex flex-col gap-12">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-36 w-full" />
      ))}
    </div>
    <GenericListSkeleton rows={4} />
  </div>
)

function OverviewPage() {
  const { t } = useTranslation()
  const { isAdmin } = useMembership()
  const organizationQuery = useOrganizationQuery()
  const statisticsQuery = useOrganizationStatistics()
  const paymentsQuery = useFriskvardPayments()

  const organization = organizationQuery.data
  const statistics = statisticsQuery.data
  const payments = paymentsQuery.data ?? []

  const isLoading =
    organizationQuery.isLoading || statisticsQuery.isLoading || paymentsQuery.isLoading
  const isError = organizationQuery.isError || statisticsQuery.isError || paymentsQuery.isError

  const activePeriod = organization?.activePeriod
  const spentPercent =
    activePeriod && activePeriod.amount > 0
      ? (activePeriod.totalBudgetSpent / activePeriod.amount) * 100
      : 0
  const allocatedBudget = organization?.totalAssignedBudget ?? 0
  const allocatedPercent =
    activePeriod && activePeriod.amount > 0 ? (allocatedBudget / activePeriod.amount) * 100 : 0

  const recentDays = 28
  const recentCutoff = subDays(new Date(), recentDays)
  const recentPayments = payments.filter(payment =>
    isAfter(new Date(payment.receiptDate), recentCutoff),
  )

  return (
    <PageLayout title={t('navigation.overview')}>
      <StateSwitcher isError={isError} isLoading={isLoading} loadingState={<OverviewSkeleton />}>
        <div className="flex flex-col gap-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KPICard
              className="col-span-1"
              title={t('overviewPage.budgetCard')}
              icon={<WalletIcon />}
              variant="accent"
              footer={
                activePeriod ? (
                  <div className="flex flex-col gap-2">
                    <Progress
                      className="border-white/30 bg-white/20"
                      value={spentPercent}
                      secondaryValue={isAdmin ? allocatedPercent : undefined}
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span>
                        {formatDate(activePeriod.startDate)} – {formatDate(activePeriod.endDate)}
                      </span>
                      {/* A manager's figures are already their departments' assigned budget, so
                          restating it as "allocated" would just repeat the denominator. */}
                      {isAdmin && (
                        <span className="tabular-nums">
                          {t('overviewPage.allocatedBudgetLabel', {
                            amount: formatKr(allocatedBudget),
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                ) : undefined
              }
            >
              {activePeriod ? (
                <span className="heading-3 tabular-nums">
                  {formatKr(activePeriod.totalBudgetSpent)} / {formatKr(activePeriod.amount)}
                </span>
              ) : (
                <span className="heading-4">{t('overviewPage.noActivePeriod')}</span>
              )}
            </KPICard>

            <KPICard
              title={t('overviewPage.friskvardCard')}
              value={formatKr(statistics?.friskvardSpentSek ?? 0)}
              icon={<ReceiptTextIcon />}
            />

            <KPICard
              title={t('overviewPage.bookingsCard')}
              value={statistics?.bookingsCount ?? 0}
              icon={<CalendarCheckIcon />}
            />
          </div>

          <section className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="heading-3">{t('overviewPage.recentFriskvardTitle')}</h2>
                <h2 className="body-2 text-text-secondary">
                  {t('overviewPage.recentFriskvardSubtitle', { cutoff: recentDays })}
                </h2>
              </div>
              <Link
                to="/friskvard"
                className="text-brand-secondary flex items-center gap-1 text-sm font-medium hover:underline"
              >
                {t('overviewPage.seeAll')}
                <ArrowRightIcon className="size-4" />
              </Link>
            </div>
            <FriskvardTable payments={recentPayments} />
          </section>
        </div>
      </StateSwitcher>
    </PageLayout>
  )
}
